import React from "react";
import { BarChart3, ExternalLink, Clock, Activity, Users, Moon, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiInstance as api } from "../../../../hooks/useApi";
import { Drawer } from "../../components/Drawer";
import { Button, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { DateRange, rangeForPreset } from "../../components/DateRange";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Shimmer } from "../../components/StatCard";
import { DASHBOARD_BASE } from "../../../../config/featureFlags";

const fmtHours = (h) => {
  const n = parseFloat(h) || 0;
  const totalMins = Math.round(n * 60);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins === 0 ? `${hrs}h` : `${hrs}h ${mins}m`;
};
const ymd = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

// Slide-in project hours — DashV2 theme only. Replaces the legacy Tailwind
// ProjectAnalytics embed that painted a second white "Team Hours" card inside
// the drawer (double header + horizontal overflow on the date picker).
export function ProjectAnalyticsPanel({ project, onClose }) {
  const t = useDashTheme();
  const navigate = useNavigate();
  const projectId = project ? project._id || project.id : null;
  const [range, setRange] = React.useState(() => rangeForPreset("today"));
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!project) setRange(rangeForPreset("today"));
  }, [project]);

  React.useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ startDate: ymd(range.start), endDate: ymd(range.end) });
        const res = await api.get(`/api/v1/projects/${projectId}/member-hours?${params}`);
        if (!alive) return;
        if (res.data?.success === false) setError(res.data?.error?.message || "Failed to load.");
        else setData(res.data?.data || null);
      } catch (err) {
        if (alive) setError(err.response?.data?.error?.message || "Failed to load.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, range.start, range.end]);

  const totals = data?.projectTotals || {};
  const members = (data?.memberHours || []).map((m) => ({
    userId: m.userId || m._id,
    avatar: m.avatar || null,
    name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Member",
    email: m.email || "",
    position: m.position || m.role || "Member",
    totalHours: m.totalHours ?? 0,
    active: m.totalActiveTimeFormatted || "0h 0m",
    idle: m.totalIdleTimeFormatted || "0h 0m",
    sessionHours: m.sessionHours ?? m.trackedHours ?? m.totalHours ?? 0,
    manualHours: m.manualHours ?? 0,
    sessions: m.sessionCount || 0,
  }));

  return (
    <Drawer
      open={!!project}
      onClose={onClose}
      title={project?.name || "Analytics"}
      subtitle="Tracked time and member breakdown"
      icon={<BarChart3 size={18} />}
      width={720}
      toolbar={
        <DateRange
          range={range}
          onChange={setRange}
          layoutId="projectAnalyticsRange"
          pickerWidth={220}
          presets={[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "7d", label: "7 days" },
            { key: "30d", label: "30 days" },
          ]}
        />
      }
      footer={
        <>
          <Button
            variant="secondary"
            leftIcon={<ExternalLink size={14} />}
            onClick={() => navigate(`${DASHBOARD_BASE}/projects/${projectId}`, { state: { project } })}
          >
            Open project
          </Button>
          <Button variant="primary" onClick={onClose}>Close</Button>
        </>
      }
    >
      {loading && !data ? (
        <PanelSkeleton t={t} />
      ) : error ? (
        <EmptyState icon={<BarChart3 size={22} />} title="Couldn’t load" description={error} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[5] }}>
          {/* auto-fit, not a fixed 3-up: cards reflow onto as many rows as the
              width allows instead of squeezing until values clip. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: t.s[3] }}>
            <Metric t={t} icon={<Clock size={13} />} label="Total" value={fmtHours(totals.totalHours)} />
            <Metric t={t} icon={<Activity size={13} />} label="Active" value={totals.totalActiveTimeFormatted || "0h 0m"} color={t.status.positive} />
            <Metric t={t} icon={<Moon size={13} />} label="Idle" value={totals.totalIdleTimeFormatted || "0h 0m"} color={t.status.warning} />
            <Metric t={t} icon={<Users size={13} />} label="Members" value={totals.activeMembers ?? members.length} />
            <Metric t={t} icon={<Zap size={13} />} label="Sessions" value={totals.totalSessions ?? 0} />
            <Metric
              t={t}
              icon={<Clock size={13} />}
              label="Range"
              value={`${ymd(range.start).slice(5)} → ${ymd(range.end).slice(5)}`}
            />
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: t.c.textSecondary, fontSize: t.font.size.sm, fontWeight: 600, marginBottom: t.s[2] }}>
              <Users size={14} /> Members
              <span style={{
                marginLeft: 4, padding: "1px 8px", borderRadius: 99, fontSize: t.font.size.xs, fontWeight: 500,
                background: t.c.surfaceMuted, color: t.c.textTertiary, border: `1px solid ${t.c.border}`,
              }}>
                {members.length}
              </span>
            </div>
            <DataTable
              rows={members}
              rowKey={(r) => r.userId}
              emptyState={<EmptyState icon={<Users size={22} />} title="No members" description="No tracked member hours in this range." />}
              columns={[
                {
                  key: "name", header: "Member",
                  render: (r) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar user={r} name={r.name} size={30} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.position}{r.email ? ` · ${r.email}` : ""}
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "totalHours", header: "Total", align: "right", width: 72,
                  render: (r) => <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtHours(r.totalHours)}</span>,
                },
                {
                  key: "active", header: "Active", align: "right", width: 72,
                  render: (r) => <span style={{ color: t.status.positive, fontVariantNumeric: "tabular-nums" }}>{r.active}</span>,
                },
                {
                  key: "idle", header: "Idle", align: "right", width: 72,
                  render: (r) => <span style={{ color: t.status.warning, fontVariantNumeric: "tabular-nums" }}>{r.idle}</span>,
                },
                {
                  key: "sessions", header: "Sess.", align: "right", width: 52,
                  render: (r) => <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{r.sessions}</span>,
                },
              ]}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
}

function Metric({ t, icon, label, value, color }) {
  return (
    <div style={{ padding: t.s[3], borderRadius: t.r.md, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: color || t.c.textTertiary, fontSize: t.font.size.xs, fontWeight: 500 }}>
        {icon}{label}
      </div>
      <div style={{ fontSize: t.font.size.lg, fontWeight: 700, color: color || t.c.text, marginTop: 5, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

function PanelSkeleton({ t }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[5] }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: t.s[3] }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ padding: t.s[3], borderRadius: t.r.md, background: t.c.surfaceMuted }}>
            <Shimmer w="50%" h={10} /><div style={{ height: 8 }} /><Shimmer w="70%" h={18} />
          </div>
        ))}
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Shimmer w={30} h={30} radius={99} /><Shimmer w="55%" h={12} />
        </div>
      ))}
    </div>
  );
}
